import axios from '../../../../api/axios';

const BASE = '/api/ee/my-drive';

/** RFC 5987：優先取 filename*=UTF-8''<pct>（中文檔名），退回 filename="…"。 */
export function parseFilenameFromDisposition(dispo: string): string | null {
  if (!dispo) return null;
  const star = dispo.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch { return star[1].trim(); }
  }
  const plain = dispo.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1].trim() : null;
}

/** 用臨時 <a download> 觸發瀏覽器下載，事後 revoke object URL。 */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export type DirCategory = 'system' | 'mail' | 'photos' | 'personal';

export interface DriveFile {
  id: number;
  dir_category: DirCategory;
  relative_path: string;
  filename: string;
  size_bytes: number;
  mime_type: string;
  scan_status: string;
  is_directory: boolean;
  created_at: string;
  updated_at: string;
}

export interface QuotaInfo {
  quota_bytes: number;
  used_bytes: number;
  file_count: number;
  quota_gb: number;
  used_gb: number;
  used_pct: number;
}

export interface TenantQuota {
  organization_id: number;
  org_name: string;
  quota_bytes: number;
  quota_gb: number;
  user_count: number;
  updated_at: string | null;
}

export interface UserQuota {
  user_id: number;
  username: string;
  account: string;
  quota_bytes: number | null;   // null = 繼承租戶
  quota_gb: number | null;
  used_bytes: number;
  used_gb: number;
}

export interface WebDAVInfo {
  enabled: boolean;
  username: string;
  url: string;
}

// 3.2e Office 健康檢查
export type OfficeHealthStatus = 'ok' | 'warn' | 'error' | 'off';
export interface OfficeHealthCheck {
  name: string;
  status: OfficeHealthStatus;
  value?: string | boolean | null;
  latency_ms?: number | null;
  hint?: string | null;
}
export interface OfficeHealth {
  office_enabled: boolean;
  checks: OfficeHealthCheck[];
  overall_status: OfficeHealthStatus;
}

const myDriveService = {
  listFiles: (dir_category: DirCategory, prefix = '') =>
    axios.get<{ items: DriveFile[]; total: number }>(`${BASE}/files`, {
      params: { dir_category, prefix },
    }).then(r => r.data),

  uploadFile: (dir_category: DirCategory, relative_path: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${BASE}/files/upload`, fd, {
      params: { dir_category, relative_path },
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  // 單檔下載：preview endpoint ?disposition=attachment → blob → <a download>
  downloadFile: async (file_id: number, fallbackName?: string) => {
    const resp = await axios.get(`${BASE}/files/${file_id}/preview`, {
      params: { disposition: 'attachment' },
      responseType: 'blob',
    });
    const dispo = (resp.headers['content-disposition'] as string) || '';
    const name = parseFilenameFromDisposition(dispo) || fallbackName || `file-${file_id}`;
    triggerBrowserDownload(resp.data as Blob, name);
  },

  // 批次下載：後端打包 zip 串流 → blob → <a download>
  downloadBatch: async (file_ids: number[]) => {
    const resp = await axios.post(`${BASE}/files/batch-download`,
      { file_ids }, { responseType: 'blob' });
    const dispo = (resp.headers['content-disposition'] as string) || '';
    const name = parseFilenameFromDisposition(dispo) || 'my-drive-batch.zip';
    triggerBrowserDownload(resp.data as Blob, name);
  },

  // 依 id 批次取檔案 metadata（附件列表用）
  getFilesMeta: (ids: number[]) =>
    ids.length === 0
      ? Promise.resolve([] as DriveFile[])
      : axios.get<DriveFile[]>(`${BASE}/files/meta`, {
          params: { ids: ids.join(',') },
        }).then(r => r.data),

  deleteFile: (file_id: number) =>
    axios.delete(`${BASE}/files/${file_id}`).then(r => r.data),

  getQuota: () =>
    axios.get<QuotaInfo>(`${BASE}/quota`).then(r => r.data),

  batchDelete: (file_ids: number[], recursive: boolean = false) =>
    axios.post<{ success_count: number }>(`${BASE}/files/batch-delete`,
      { file_ids, recursive }).then(r => r.data),

  batchMove: (file_ids: number[], target_dir: string) =>
    axios.post<{ success_count: number }>(`${BASE}/files/batch-move`,
      { file_ids, target_dir }).then(r => r.data),

  copyFile: (file_id: number, target_relative_path?: string) =>
    axios.post<DriveFile>(
      `${BASE}/files/${file_id}/copy`,
      target_relative_path ? { target_relative_path } : {},
    ).then(r => r.data),

  createFolder: (dir_category: DirCategory, relative_path: string) =>
    axios.post<DriveFile>(`${BASE}/folders`, { dir_category, relative_path }).then(r => r.data),

  // Task-33 3.2b · 線上編輯：legacy → file_object 橋接（fast/slow path），再簽 WOPI token
  promoteToEditable: (fileId: number) =>
    axios.post<{ file_object_id: number; promoted: boolean }>(
      `${BASE}/files/${fileId}/promote-to-editable`).then(r => r.data),
  // permission 省略 → backend auto-decide（owner→edit）；傳 'read' → view mode 降級
  issueOfficeToken: (fileObjectId: number, permission?: 'read' | 'edit') =>
    axios.post<{ token: string; collabora_url: string; expires_in: number; permission: string }>(
      `${BASE}/files/${fileObjectId}/office-token`, permission ? { permission } : {}).then(r => r.data),
  // 3.2e Office 服務健康檢查（tenant scope）→ {office_enabled, checks[], overall_status}
  officeHealthCheck: () =>
    axios.get<OfficeHealth>(`${BASE}/office-health`).then(r => r.data),

  // 3.2c 新建空白 Office 檔 → 回 file_object_id（前端直接 window.open /office/{id}）
  createNewOfficeFile: (payload: {
    file_type: 'docx' | 'xlsx' | 'pptx'; filename: string; dir_category: string; relative_path: string;
  }) =>
    axios.post<{ file_id: number; file_object_id: number; filename: string }>(
      `${BASE}/files/new-office-file`, payload).then(r => r.data),

  renameFile: (file_id: number, new_filename: string) =>
    axios.patch<DriveFile>(`${BASE}/files/${file_id}`, { new_filename }).then(r => r.data),

  moveFile: (file_id: number, new_relative_path: string) =>
    axios.patch<DriveFile>(`${BASE}/files/${file_id}`, { new_relative_path }).then(r => r.data),

  deleteFileRecursive: (file_id: number) =>
    axios.delete(`${BASE}/files/${file_id}`, { params: { recursive: true } }).then(r => r.data),

  // WebDAV
  getWebdavInfo: () =>
    axios.get<WebDAVInfo>(`${BASE}/webdav/info`).then(r => r.data),

  enableWebdav: (password: string) =>
    axios.post<WebDAVInfo>(`${BASE}/webdav/enable`, { password }).then(r => r.data),

  disableWebdav: () =>
    axios.post(`${BASE}/webdav/disable`, {}).then(r => r.data),

  // Admin — 租戶配額
  listTenantQuotas: () =>
    axios.get<TenantQuota[]>(`${BASE}/admin/tenant-quota`).then(r => r.data),

  updateTenantQuota: (org_id: number, quota_gb: number) =>
    axios.put<TenantQuota>(`${BASE}/admin/tenant-quota/${org_id}`, { quota_gb }).then(r => r.data),

  // Admin — 個人配額
  listOrgUsers: (org_id: number) =>
    axios.get<UserQuota[]>(`${BASE}/admin/tenant-quota/${org_id}/users`).then(r => r.data),

  updateUserQuota: (user_id: number, quota_gb: number | null) =>
    axios.put<UserQuota>(`${BASE}/admin/user-quota/${user_id}`, { quota_gb }).then(r => r.data),
};

export default myDriveService;

// ── 線上編輯可支援的檔型（.docx/.xlsx/.pptx/.pdf）· context menu「線上編輯」gate ──────
const _EDITABLE_MIMES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',    // .docx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',          // .xlsx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',  // .pptx
  'application/pdf',                                                            // .pdf（view/comment）
]);
export const isEditableMime = (mime?: string | null): boolean => !!mime && _EDITABLE_MIMES.has(mime);

// 只有 docx/xlsx/pptx（不含 pdf）· 檢視要改走 Collabora view（browser 無法 iframe render OOXML）；
// pdf 仍走現況 modal（browser native PDF viewer 可 render）。
const _OFFICE_DOC_MIMES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);
export const isOfficeDoc = (mime?: string | null): boolean => !!mime && _OFFICE_DOC_MIMES.has(mime);

// ── 資料夾上傳（階段 1 · frontend-only · porsche 2026-08-27）──────────────────────
// webkitdirectory 選/拖整個資料夾 → 解析 webkitRelativePath → 去重建 subfolder → 逐檔上傳
// （走現有 POST /folders + /files/upload，backend 零改動）。某檔失敗續傳其他，最後回總結。
export interface FolderUploadResult {
  success: number;
  failed: { name: string; reason: string }[];
}

const _relPath = (f: File): string =>
  (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;

export async function uploadFolderFiles(
  dirCategory: DirCategory,
  basePrefix: string,                                   // 當前資料夾（'' = 根；可含或不含結尾 /）
  files: File[],
  onProgress?: (done: number, total: number) => void,
): Promise<FolderUploadResult> {
  const base = (basePrefix || '').replace(/\/+$/, '');  // 去尾 /

  // 1. 蒐集每檔祖先目錄 → 去重 → 淺到深排序建 subfolder（已存在視為成功）
  const folders = new Set<string>();
  for (const f of files) {
    const parts = _relPath(f).split('/').slice(0, -1);  // 去掉檔名
    let acc = base;
    for (const p of parts) {
      acc = acc ? `${acc}/${p}` : p;
      folders.add(acc);
    }
  }
  const ordered = Array.from(folders).sort((a, b) => a.split('/').length - b.split('/').length);
  for (const path of ordered) {
    try { await myDriveService.createFolder(dirCategory, path); }
    catch { /* silent ok: 資料夾已存在（重複上傳同結構）· 去重視為成功 */ }
  }

  // 2. 逐檔上傳；某檔失敗不中斷，收集 failed
  const failed: { name: string; reason: string }[] = [];
  let done = 0;
  for (const f of files) {
    const full = base ? `${base}/${_relPath(f)}` : _relPath(f);
    try {
      await myDriveService.uploadFile(dirCategory, full, f);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const code = typeof detail === 'string' ? detail : (detail as { code?: string })?.code;
      failed.push({ name: _relPath(f), reason: code || 'upload_failed' });
    }
    done += 1;
    onProgress?.(done, files.length);
  }
  return { success: files.length - failed.length, failed };
}
